import { Box, Button, Divider, FormField, Input } from '@wix/design-system'
import { useMetadata } from '@wix/funnel-builder-lizard'
import { EditSmall } from '@wix/wix-ui-icons-common'
import cs from 'classnames'
import React, { ChangeEventHandler, FC, MouseEventHandler, useEffect, useMemo, useState } from 'react'

import { ambassadorHttpClient } from '../../../../api/httpClient'
import { usePageLogger } from '../../../../bi/mfi'
import { BusinessTermService } from '../../../reusable/Category/api/BusinessTermService'
import { loadRecommendedApps } from '../actions/loadRecommendedApps'
import { useSecondTryContext } from '../SecondTryContext/SecondTryContext'
import { TSecondTry } from '../typings/SecondTry.content'
import { WidgetAppsMultiSelect } from '../WidgetAppsMultiSelect/WidgetAppsMultiSelect'
import { WidgetCategoryAutoComplete } from '../WidgetCategoryAutoComplete/WidgetCategoryAutoComplete'
import s from './Widget.scss'

type WidgetProps = {
  content: TSecondTry['widget']['content']
}

const MIN_APPS_LOADING_DELAY = 1000

export const Widget: FC<WidgetProps> = ({ content }) => {
  const pageLogger = usePageLogger()
  const [{ biSlug: flowBiSlug }] = useMetadata()
  const [isActive, setIsActive] = useState(false)
  const {
    businessName,
    categoryState,
    introData,
    selectedAppDefIds,
    dispatch,
    isRecommendedAppsLoading,
    recommendedApps,
  } = useSecondTryContext()
  const { businessTerm = {} } = categoryState.category ?? {}
  const industryId = businessTerm.industryId || introData.category?.industryId || ''
  const structureId = businessTerm.structureId || introData.category?.structureId || ''

  const businessTermService = useMemo(() => new BusinessTermService(ambassadorHttpClient), [])

  useEffect(() => {
    if (!industryId || !structureId) {
      return
    }

    void loadRecommendedApps({
      dispatch,
      flowBiSlug,
      industryId,
      structureId,
      selectedAppDefIds,
      minDelay: MIN_APPS_LOADING_DELAY,
      contentApps: content.apps.options,
    })
  }, [industryId, structureId])

  const handleEditClick: MouseEventHandler = () => {
    setIsActive(true)
    void pageLogger.generalAction({ action: 'general_edit' })
  }

  const handleBusinessNameChange: ChangeEventHandler<HTMLInputElement> = e => {
    dispatch({ type: 'setBusinessName', businessName: e.target.value })
    void pageLogger.generalAction({ action: 'edit_name', questionKey: content.businessName.questionKey })
  }

  const displayedBusinessName = businessName ? businessName : content.businessName.content.fallbackName
  return (
    <Box width="65%" direction="vertical" gap="SP4">
      <section className={s.container}>
        <header className={s.header}>
          <div className={s.title}>{content.header}</div>
          {!isActive && (
            <Button onClick={handleEditClick} priority="secondary" size="small" prefixIcon={<EditSmall />}>
              {content.editBtn}
            </Button>
          )}
        </header>
        <Divider />
        <div className={s.content}>
          {!isActive ? (
            <div className={s.wrapper}>
              <span className={s.businessName}>{displayedBusinessName}</span>
              <div className={s.formField}>
                <Divider className={s.divider} />
                <div className={s.textWrapper}>
                  <span className={s.label}>{content.category.content.label}</span>
                  <span className={s.value}>
                    {(businessTerm.displayName ?? introData.category?.displayName) || '-'}
                  </span>
                </div>
              </div>
              <div className={s.formField}>
                <Divider className={s.divider} />
                <div className={s.textWrapper}>
                  <span className={s.label}>{content.apps.content.label}</span>
                  <span className={s.value}>
                    {!isRecommendedAppsLoading ? (
                      selectedAppDefIds && selectedAppDefIds.length > 0 ? (
                        recommendedApps
                          .filter(({ appDefId }) => selectedAppDefIds.includes(appDefId))
                          .map(({ name }) => name)
                          .join(', ')
                      ) : (
                        '-'
                      )
                    ) : (
                      <span className={s.ellipsis}>
                        <span className={s.dot} />
                        <span className={s.dot} />
                        <span className={s.dot} />
                      </span>
                    )}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className={cs(s.wrapper, s.active)}>
              <FormField label={<span className={s.label}>{content.businessName.content.label}</span>}>
                <Input
                  placeholder={content.businessName.content.placeholder}
                  onChange={handleBusinessNameChange}
                  value={businessName}
                  size="small"
                />
              </FormField>
              <FormField label={<span className={s.label}>{content.category.content.label}</span>}>
                <WidgetCategoryAutoComplete
                  placeholder={content.category.content.placeholder}
                  questionKey={content.category.questionKey}
                  businessTermService={businessTermService}
                />
              </FormField>
              <FormField label={<span className={s.label}>{content.apps.content.label}</span>}>
                <WidgetAppsMultiSelect question={content.apps} />
              </FormField>
            </div>
          )}
        </div>
      </section>
    </Box>
  )
}
